import { Vector2 } from '../../../odie';
import { app } from '../../app';

import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import {
  despawnBlockEntity,
  FuguBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { BeeScatteringEffect } from '../effects/BeeScatteringEffect';
import { FuguEffect } from '../effects/FuguEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { SmokeEffect } from '../effects/SmokeEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    fugu block collision resolver
*/
export class FuguCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: FuguBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: FuguBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap() {
    await new InvalidEffect(
      this._entity,
      this._entity.c.blockFugu.view,
    ).execute();
    return false;
  }

  private async _resolveBlock() {
    await this._attack();
    return true;
  }

  private async _resolveAttack(event: CollideEvent) {
    await this._attack(isOneHitCollider(event.collider?.type));
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack(destroyFully = false) {
    app.sound.play('puzzle-fugu-poke', {
      pan: mapGetPan(this._entity.c.position.mapPosition),
    });
    this._entity.c.blockFugu.view.bees.removeBees();
    const position = this._entity.c.position.mapPosition;

    if (destroyFully) {
      // Fugu goals are tracked as miniFugu, so full-destroy paths must grant that goal explicitly.
      this._scene.sessionSystem.triggerGoal('miniFugu', this._entity);
      await this._breakBlock(position);
      return;
    }

    await Promise.all([
      this._breakBlock(position),
      new FuguEffect(this._scene, {
        subject: this._entity,
      }).execute(),
    ]);
  }

  private async _breakBlock(position: Vector2) {
    despawnBlockEntity(this._scene, this._entity);
    await Promise.all([
      new SmokeEffect(this._scene, { position, tint: 0xfff7d7 }).execute(),
      new BeeScatteringEffect(this._scene, { position }).execute(),
    ]);
  }
}
